import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { UserPlus, X, Search } from "lucide-react";

const nameOf = (s) => `${s.last_name || ""} ${s.first_name || ""}`.trim();

export function SessionStudentsPicker({ years, exclude, extra, onChange }) {
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const { data: students = [] } = useQuery({
    queryKey: ["students-active-att"],
    queryFn: async () => (await api.get("/students", { params: { status: "attivo" } })).data,
  });
  const base = useMemo(() => students
    .filter((s) => s.track_type === "residenziale" && (!years.length || years.includes(Number(s.year_level))))
    .sort((a, b) => nameOf(a).localeCompare(nameOf(b))), [students, years]);
  const baseIds = new Set(base.map((s) => s.id));
  const others = students.filter((s) => !baseIds.has(s.id) && !extra.includes(s.id) && (!q || nameOf(s).toLowerCase().includes(q.toLowerCase())));
  const extras = students.filter((s) => extra.includes(s.id));
  const toggle = (id, on) => onChange({ exclude: on ? exclude.filter((x) => x !== id) : [...exclude, id], extra });

  return (
    <div className="rounded-lg border border-stone-200" data-testid="session-students-picker">
      <div className="px-3 py-2 border-b border-stone-100 text-xs flex items-center gap-2">
        <b>Studenti a registro:</b> {base.length - exclude.filter((x) => baseIds.has(x)).length + extras.length}
        {exclude.length > 0 && <span className="text-red-600">· {exclude.length} esclusi</span>}
        {extras.length > 0 && <span className="text-emerald-700">· {extras.length} aggiunti</span>}
      </div>
      <div className="max-h-48 overflow-y-auto divide-y divide-stone-50">
        {base.map((s) => (
          <label key={s.id} className={`flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer ${exclude.includes(s.id) ? "text-stone-400 line-through" : ""}`}>
            <Checkbox checked={!exclude.includes(s.id)} onCheckedChange={(v) => toggle(s.id, !!v)} data-testid={`session-stud-${s.id}`}/>
            <span className="flex-1">{nameOf(s)}</span>
            <span className="text-[10px] text-stone-400">{s.year_level ? `${s.year_level}° anno` : ""}</span>
          </label>
        ))}
        {extras.map((s) => (
          <div key={s.id} className="flex items-center gap-2 px-3 py-1.5 text-sm bg-emerald-50/50">
            <UserPlus size={13} className="text-emerald-700"/><span className="flex-1">{nameOf(s)}</span>
            <span className="text-[10px] text-stone-400">{s.track_type}{s.year_level ? ` · ${s.year_level}°` : ""}</span>
            <button type="button" onClick={() => onChange({ exclude, extra: extra.filter((x) => x !== s.id) })} className="text-stone-400 hover:text-red-600" data-testid={`session-extra-remove-${s.id}`}><X size={13}/></button>
          </div>
        ))}
      </div>
      <div className="p-2 border-t border-stone-100">
        {!adding ? (
          <Button type="button" variant="ghost" size="sm" onClick={() => setAdding(true)} data-testid="session-add-student"><UserPlus size={13} className="mr-1"/> Aggiungi altri studenti</Button>
        ) : (
          <div>
            <div className="relative"><Search size={13} className="absolute left-2 top-2.5 text-stone-400"/>
              <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca studente (anche modulari / altri anni)…" className="h-8 pl-7 text-sm" data-testid="session-add-search"/></div>
            <div className="max-h-32 overflow-y-auto mt-1">
              {others.slice(0, 30).map((s) => (
                <button type="button" key={s.id} onClick={() => onChange({ exclude, extra: [...extra, s.id] })} data-testid={`session-add-${s.id}`}
                  className="w-full text-left px-2 py-1 text-sm hover:bg-stone-50 rounded flex justify-between">
                  <span>{nameOf(s)}</span><span className="text-[10px] text-stone-400">{s.track_type}{s.year_level ? ` · ${s.year_level}°` : ""}</span>
                </button>
              ))}
              {others.length === 0 && <div className="text-[11px] text-stone-400 px-2 py-1">Nessuno studente trovato.</div>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
